import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import { getAuthUser } from "../../../helpers/utils";
import { useDispatch, useSelector } from "react-redux";
import { deleteDocuments, fetchAllDocument, fetchDocumentTableColumnNames } from "../../../slices/AIDoc/thunk"; 
import { updateUserPreference} from "../../../slices/thunks"; 
import DeleteModal from "../../../Components/Common/DeleteModal";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import usePermissions from "../../../Components/Hooks/usePermissions";
import { fetchAllTags, fetchCategories, uploadDocumentFile, updateDocument } from "../../../slices/AIDoc/thunk";
import { updateFileProcessingStatus } from "../../../slices/AIDoc/reducer";

function BookManagement() {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const {user} = getAuthUser();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false); 
  const [modalOpen, setModalOpen] = useState(false);  
  const [selectedColumns, setSelectedColumns] = useState([]);

  const { 
    documents, 
    documentsLoading, 
    Headerloading,
    loading,
    documentsError, 
    tableColumnNames, 
   } = useSelector(
    (state) => ({
      documents: state.AIDoc.documents,
      documentsLoading: state.AIDoc.documentsLoading,
      Headerloading: state.AIDoc.Headerloading,
      loading: state.AIDoc.loading,
      documentsError: state.AIDoc.documentsError,
      tableColumnNames: state.AIDoc.tableColumnNames, 
    })
  );
  

  // Compute permission
  const permission = (() => {
    return usePermissions(2, 7);
  })();

  const onProgress = ({ event, data }) => {
    try {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;

      if (event === "file_ocr_done") {
        const { reqBody = {} } = parsedData;
        const data = {
          DocumentID: parsedData.DBID,
          OriginFileName: parsedData.text?.originalFileName,   
          Tags: parsedData.text?.tags
        }
        
        dispatch(updateDocument(data)).unwrap()
        .then(() => {
          console.log("saved successfully");
        })
        .catch((error) => {
          console.error("save failed:", error);
        });
      }
      
      if (event === "file_processed") {
        const { DBID, url, reqBody = {} } = parsedData;
        dispatch(updateFileProcessingStatus({ ID: DBID, JsonURL: url, IsProcessed: true, ModifiedByID: user.ID }))
      }
      
    } catch (error) {
      console.error("Failed to process stream event:", error, data);
    }
  };

  const handleViewClick = (item) => {
    if (item && item.FileURL) {
      window.open(item.FileURL, '_blank', 'noopener,noreferrer');
    } else {
      toast.error(t("FileURLNotAvailable", "File URL is not available for this document."));
    }
  };

  const handleButtonClick = async (item, title, type) => {
    if (type == "view") {
      navigate('/ai/book-details', { 
        state:  {
          DocID: item.ID,
          FileURL: item.FileURL,
          JsonURL: item.JsonURL,
          IsSaved: item.IsSaved,
          ReviewedBy: item.ReviewedBy,
          DocumentTypeID: item.DocumentTypeID
        }
      })
    } else if (type == 'get-json' || type == 'retry') {
      try {
        const formData = new FormData();
        dispatch(updateFileProcessingStatus({ ID: item.ID, JsonURL: null, IsProcessed: false, ModifiedByID: user.ID}))
        formData.append("IsBook", true);
        formData.append("DBID", item.ID);
        formData.append("fileUrl", item.FileURL);
        formData.append("DocumentTypeID", 5);
        // formData.append("IsLargeFile", false);
        formData.append("SaveJSON", true);
        
        dispatch(uploadDocumentFile({ formData, onProgress, isMultiUpload: false }));
      } catch (error) {
        console.error("Error in handleButtonClick:", error);
      }
    }
  }

  const handleDeleteActionClick = (item) => {
    setSelectedIds([item.ID || item.Id]);
    setDeleteModal(true);
  };

  const actionConfig = {
    view: { 
      icon: "ri-eye-line",
      tooltip: t("View"),
      onClick: handleViewClick,
      isVisible: (item, perm) => true, // Show for all items, or add permission check: perm?.CanView
    },
    delete: {
      icon: "ri-delete-bin-2-line",
      tooltip: t("Delete"),
      onClick: handleDeleteActionClick,
      isVisible: (item, perm) => perm?.CanDelete,
    },
  };

  const confirmDelete = () => {
    dispatch(deleteDocuments({ DocumentIDs: selectedIds, DocumentTypeID: 5 })) 
      .then(() => {
        toast.success(t("Documents deleted successfully"));
        setSelectedIds([]);
      })
      .catch((error) => {
        console.error("Error deleting:", error);
      });
    setDeleteModal(false);
  };

  const saveUserPreferences = async () => {
    const preferencesPayload = {
      ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ID] = selectedColumns.includes(column.ID);
        return acc;
      }, {}),
    };

    setModalOpen(false);
    try {
      await dispatch(updateUserPreference(preferencesPayload)).unwrap(); 
      await dispatch(
        fetchDocumentTableColumnNames({ 
          module: "AI_Book", 
        }) 
      );

    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchDocumentTableColumnNames({ module: "AI_Book" })).unwrap(); 
         dispatch(fetchAllDocument({ DocumentTypeID: 5, IsSaved: [1, 4, 98].includes(user.RoleID) ? false : true })); 
        dispatch(fetchCategories());
        dispatch(fetchAllTags());
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [dispatch]); 



  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadCrumb title={t("Books")} />
          </Col>
        </Row>
        <div className="table-responsive">
          <Card>
            <CardHeader>
              <Row className="g-4">
                    <Col className="col-sm-auto">
                      <div>
                        <Button
                          className="btn btn-soft-danger ms-2 me-2"
                          onClick={() => setDeleteModal(true)}
                          disabled={selectedIds.length === 0 || !permission.CanDelete}
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </Button>
                          <Button
                            color="secondary"
                            className="add-btn me-1"
                            onClick={() => navigate("/ai/book-upload")}
                            id="create-btn"
                          >
                            <i className="ri-upload-cloud-2-line align-bottom me-1"></i> {/* Icon for upload */}
                            {t("Upload Book")}
                          </Button>
                      </div>
                    </Col>
                    <Col className="col-sm">
                      <div className="d-flex justify-content-sm-end">
                        <Button
                          className="btn btn-soft"
                          onClick={() => setModalOpen(true)}
                          disabled={documentsLoading} // Use documentsLoading
                        >
                          <i className="ri-filter-3-line"></i>
                        </Button>
                        <div className="search-box ms-2 me-2">
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={t("Search")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <i className="ri-search-line search-icon"></i>
                        </div>
                      </div>
                    </Col>
              </Row>
            </CardHeader>

            <CardBody>
              <div className="listjs-table" id="customerList">
              
                {documentsLoading || Headerloading ? (
                  <div className="d-flex justify-content-center align-items-center">
                    <Spinner
                      style={{ height: "3rem", width: "3rem" }}
                      className="me-2"
                    >
                      Loading...
                    </Spinner>
                  </div>
                ) : (
                  <div className="listjs-table" id="customerList">
                    <CommonDataTable
                      data={documents} 
                      tableColumnNames={Array.isArray(tableColumnNames) ? tableColumnNames : []} // Ensure tableColumnNames is an array
                      searchTerm={searchTerm}
                      // onEditClickHandler={onEditClickHandler}
                      // onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                      handleButtonClick={handleButtonClick}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      actionConfig={actionConfig}
                      permission={permission}
                    />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </Container>

      

      {/* ColumnSelectionModal */}
      <ColumnSelectionModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        columns={Array.isArray(tableColumnNames) ? tableColumnNames : []} // Ensure columns is an array
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={saveUserPreferences}
      />

      {/* Delete modal  */}
      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDelete}
        setSelectedIds={setSelectedIds}
        description={t("confirmDelete")}
      />
    </div>
  );
}

export default BookManagement;
